import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import './SEOHelpers.css';

/**
 * Hook para gerenciar meta tags dinamicamente
 */
export const useSEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  author = 'Casal Resolve',
  publishedTime,
  modifiedTime
}) => {
  useEffect(() => {
    // Atualizar título da página
    if (title) {
      document.title = `${title} | Casal Resolve`;
    }

    // Função para atualizar ou criar meta tag
    const updateMetaTag = (name, content, property = false) => {
      if (!content) return;

      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector);

      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }

      meta.setAttribute('content', content);
    };

    // Meta tags básicas
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', author);

    // Open Graph
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:url', url, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('og:site_name', 'Casal Resolve', true);

    // Twitter Cards
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);

    // Article meta tags (para blog posts)
    if (type === 'article') {
      updateMetaTag('article:author', author, true);
      updateMetaTag('article:published_time', publishedTime, true);
      updateMetaTag('article:modified_time', modifiedTime, true);
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (url) {
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', url);
    }

  }, [title, description, keywords, image, url, type, author, publishedTime, modifiedTime]);
};

/**
 * Componente para Structured Data (JSON-LD)
 */
export const StructuredData = ({ data }) => {
  useEffect(() => {
    // Verificações rigorosas de validação
    if (!data) {
      console.warn('StructuredData: data is required');
      return;
    }

    if (typeof data !== 'object' || data === null || Array.isArray(data)) {
      console.warn('StructuredData: data must be a valid object');
      return;
    }

    try {
      // Verificar se o objeto tem propriedades válidas
      const hasValidProps = Object.keys(data).length > 0;
      if (!hasValidProps) {
        console.warn('StructuredData: data object is empty');
        return;
      }

      // Tentar serializar para detectar referencias circulares ou valores inválidos
      let jsonString;
      try {
        jsonString = JSON.stringify(data, null, 0);
      } catch (stringifyError) {
        console.error('StructuredData: JSON stringify failed:', stringifyError);
        return;
      }

      // Verificar se a serialização resultou em um JSON válido e não vazio
      if (!jsonString || jsonString === '{}' || jsonString === 'null' || jsonString === 'undefined') {
        console.warn('StructuredData: serialized data is invalid or empty');
        return;
      }

      // Remover structured data anterior
      try {
        const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
        existingScripts.forEach(script => {
          if (script.parentNode) {
            script.parentNode.removeChild(script);
          }
        });
      } catch (removeError) {
        console.warn('StructuredData: error removing existing scripts:', removeError);
      }

      // Adicionar novo structured data
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = jsonString;

      // Verificar se document.head existe
      if (document.head) {
        document.head.appendChild(script);
      } else {
        console.warn('StructuredData: document.head not available');
        return;
      }

      // Cleanup function
      return () => {
        try {
          if (script && script.parentNode) {
            script.parentNode.removeChild(script);
          }
        } catch (cleanupError) {
          console.warn('StructuredData cleanup error:', cleanupError);
        }
      };
    } catch (error) {
      console.error('StructuredData effect error:', error);
    }
  }, [data]);

  return null;
};

StructuredData.propTypes = {
  data: PropTypes.object
};

/**
 * Componente SEO completo para páginas
 */
export const SEOHead = ({
  title,
  description,
  keywords,
  image = '/og-image.png',
  url,
  type = 'website',
  structuredData,
  children
}) => {
  // Verificações de segurança para prevenir renderização de objetos
  if (typeof title !== 'string' || typeof description !== 'string') {
    console.warn('SEOHead: title and description must be strings');
    return children || null;
  }

  try {
    // Usar o hook de SEO
    useSEO({
      title,
      description,
      keywords: typeof keywords === 'string' ? keywords : '',
      image: typeof image === 'string' ? image : '/og-image.png',
      url: url || (typeof window !== 'undefined' ? window.location.href : ''),
      type: typeof type === 'string' ? type : 'website'
    });

    return (
      <>
        {structuredData && typeof structuredData === 'object' && (
          <StructuredData data={structuredData} />
        )}
        {children}
      </>
    );
  } catch (error) {
    console.error('SEOHead render error:', error);
    return children || null;
  }
};

SEOHead.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  keywords: PropTypes.string,
  image: PropTypes.string,
  url: PropTypes.string,
  type: PropTypes.string,
  structuredData: PropTypes.object,
  children: PropTypes.node
};

/**
 * Dados estruturados para a organização
 */
export const getOrganizationStructuredData = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Casal Resolve",
  "description": "Plataforma de resolução de conflitos para casais com IA",
  "url": "https://casal-resolve.vercel.app",
  "logo": "https://casal-resolve.vercel.app/logo.png",
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "availableLanguage": "Portuguese"
  },
  "sameAs": [
    "https://facebook.com/casalresolve",
    "https://twitter.com/casalresolve",
    "https://instagram.com/casalresolve"
  ]
});

/**
 * Dados estruturados para aplicativo web
 */
export const getWebApplicationStructuredData = () => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "Casal Resolve",
  "description": "Plataforma de resolução de conflitos para casais com IA. Melhore seu relacionamento com sugestões personalizadas e análises comportamentais.",
  "url": "https://casal-resolve.vercel.app",
  "applicationCategory": "LifestyleApplication",
  "operatingSystem": "Web Browser",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "BRL"
  },
  "author": {
    "@type": "Organization",
    "name": "Casal Resolve"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "ratingCount": "150"
  }
});

/**
 * Dados estruturados para artigo/blog post
 */
export const getArticleStructuredData = (article) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": article.title,
  "description": article.description,
  "image": article.image,
  "author": {
    "@type": "Person",
    "name": article.author || "Equipe Casal Resolve"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Casal Resolve",
    "logo": {
      "@type": "ImageObject",
      "url": "https://casal-resolve.vercel.app/logo.png"
    }
  },
  "datePublished": article.publishedTime,
  "dateModified": article.modifiedTime || article.publishedTime
});

/**
 * Dados estruturados para FAQ
 */
export const getFAQStructuredData = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

/**
 * Hook para breadcrumbs
 */
export const useBreadcrumbs = (breadcrumbs) => {
  useEffect(() => {
    if (!breadcrumbs || breadcrumbs.length === 0) return;

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": crumb.url
      }))
    };

    // Remover breadcrumb anterior
    const existingBreadcrumb = document.querySelector('script[data-breadcrumb="true"]');
    if (existingBreadcrumb) {
      existingBreadcrumb.remove();
    }

    // Adicionar novo breadcrumb
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-breadcrumb', 'true');
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('script[data-breadcrumb="true"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [breadcrumbs]);
};

/**
 * Componente Breadcrumb visual
 */
export const Breadcrumb = ({ items }) => {
  // Usar hook para structured data
  useBreadcrumbs(items);

  if (!items || items.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className="breadcrumb">
      <ol className="breadcrumb-list">
        {items.map((item, index) => (
          <li key={index} className="breadcrumb-item">
            {index < items.length - 1 ? (
              <a href={item.url} className="breadcrumb-link">
                {item.name}
              </a>
            ) : (
              <span className="breadcrumb-current" aria-current="page">
                {item.name}
              </span>
            )}
            {index < items.length - 1 && (
              <span className="breadcrumb-separator" aria-hidden="true">
                /
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

Breadcrumb.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired
    })
  )
};

/**
 * Função para gerar sitemap.xml (para uso no backend)
 */
export const generateSitemap = (pages) => {
  const baseUrl = 'https://casal-resolve.vercel.app';

  const urls = pages.map(page => `
    <url>
      <loc>${baseUrl}${page.path}</loc>
      <lastmod>${page.lastmod || new Date().toISOString().split('T')[0]}</lastmod>
      <changefreq>${page.changefreq || 'weekly'}</changefreq>
      <priority>${page.priority || '0.8'}</priority>
    </url>
  `).join('');

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  ${urls}
</urlset>`;
};

/**
 * Páginas padrão para sitemap
 */
export const defaultSitemapPages = [
  {
    path: '/',
    priority: '1.0',
    changefreq: 'daily'
  },
  {
    path: '/login',
    priority: '0.8',
    changefreq: 'monthly'
  },
  {
    path: '/register',
    priority: '0.8',
    changefreq: 'monthly'
  },
  {
    path: '/dashboard',
    priority: '0.9',
    changefreq: 'daily'
  },
  {
    path: '/suggestions',
    priority: '0.9',
    changefreq: 'weekly'
  },
  {
    path: '/analytics',
    priority: '0.7',
    changefreq: 'weekly'
  }
]; 